import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { MODULE_STATUS } from 'constants/modules';
import {
  AuthorizationErrorResponse,
  SocialLoginDoneResponse,
  SocialLoginEmptyReason,
} from 'types/SocialLogin';
import { ModuleStatus } from 'types/ModuleStatus';
import { NewAddedPage } from 'types/Page';
import Modal from 'components/shared/Modal';
import { ActivityIndicator, Alert, Button } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import GenericProfilesSelectForm from 'components/modals/newSocialMediaProfile/automaticForm/GenericProfilesSelectForm';
import { useQuery } from 'react-query';
import { StringParam, useQueryParams } from 'use-query-params';
import { queryKey } from 'constants/queryKey';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { AuthorizedAccountsApi } from 'app/modules/settings/api/authorizedAccounts';
import { useToast } from 'app/hooks/useToast';
import ErrorStepsGuide from '../ErrorStepsGuide';
import { ApiClientError } from 'api/client';
import HeapUtils from 'app/utils/heap';
import ManualSystemUserTokenForm from 'components/modals/newSocialMediaProfile/manualToken/ManualSystemUserTokenForm';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import {
  isClientTimeout,
  oboOutcomeHelpUrl,
  oboOutcomeMessageKey,
} from 'app/modules/socialLogin/utils/oboOutcome';
import { openBlank } from 'utils/url';
import {
  hasOboPicker,
  isOboEmptyResult,
  toOboPicker,
} from 'app/modules/socialLogin/utils/oboPicker';
import useTrackOboPortfoliosListed from 'app/modules/socialLogin/hooks/useTrackOboPortfoliosListed';
import TwoColumnPicker from 'components/modals/newSocialMediaProfile/selectSocialProfiles/twoColumn/TwoColumnPicker';
import OboEmptyState from 'components/modals/newSocialMediaProfile/automaticForm/OboEmptyState';

interface Props {
  status: ModuleStatus;
  pageType: number;
  pageId?: number;
  authorization?: SocialLoginDoneResponse;
  error?: AuthorizationErrorResponse;
  onPageAdded(addedPage: NewAddedPage): void;
  onGoBack(): void;
  isSingleSelect?: boolean;
  isLoading?: boolean;
  // The picker was reached from a Reconnect/Refresh entry, not a plain add.
  isReconnect?: boolean;
  // Why a finished Safe Connect poll listed nothing (the hosts spread the
  // social-login context in). Missing or unknown reads as having no business
  // portfolio (OboEmptyState).
  emptyReason?: SocialLoginEmptyReason | null;
}

// Safe Connect's retry comes back to this page, minus the userKey of the poll
// that just ended: left in the URL it would reload that same empty result
// instead of starting a fresh one. The path, the other parameters and the hash
// (the modal's route) stay.
const redirectWithoutUserKey = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete('userKey');

  return url.toString();
};

const Form = (props: Props) => {
  const { t } = useTranslation();
  const [showManualTokenForm, setShowManualTokenForm] = useState(false);
  const connectedPagesExternalIds = useQuery(
    queryKey.socialLoginConnectedPagesExternalIds(),
    () => SocialLoginApi.connectedPagesExternalIds(props.pageType),
    {
      onError: (error: ApiClientError) => {
        HeapUtils.track(
          HeapUtils.events.SOCIAL_LOGIN_FETCH_CONNECTED_EXTERNAL_PAGES_ERROR,
          {
            page_type: props.pageType,
            error_reason: error?.message
              ? error.message
              : error?.userMessage ?? 'Unknown error',
          },
        );
      },
    },
  );
  // A reconnect from one profile names it by our id; the pickers tick by Meta's.
  // Unknown (the lookup failed, or no such page) leaves the connected pages ticked.
  const targetPageQuery = useQuery(
    queryKey.authorizationPages('all'),
    () => AuthorizedAccountsApi.getPages('all'),
    { enabled: !!props.isReconnect && !!props.pageId },
  );
  const targetExternalId =
    targetPageQuery.data?.find((page) => page.pageId === props.pageId)
      ?.remoteId ?? null;
  const isLoadingTicks =
    connectedPagesExternalIds.isLoading || targetPageQuery.isLoading;
  const toast = useToast();
  const [, setQuery] = useQueryParams({ userKey: StringParam });
  const {
    businesses,
    outsidePages,
    partial,
    userKey,
    clientBusinessId,
    authorize,
  } = useSocialLoginContext();
  const oboPicker = useMemo(
    () => toOboPicker({ businesses, outsidePages, partial }),
    [businesses, outsidePages, partial],
  );

  // Key the OBO UI off the poll-provided `businesses` and outside pages
  // (returned only for the OBO flow and persisted across the OAuth full-page
  // redirect via the userKey in the URL) — NOT off `activeTokenType`, which is
  // ephemeral useState wiped by the redirect back from Facebook.
  const isObo = hasOboPicker({ businesses, outsidePages });
  const isOboListed = isObo && !isLoadingTicks;
  // `businesses` is an array (empty or not) only for a Safe Connect poll. One
  // that is done and lists no portfolio and no outside page has nothing to
  // pick from, so the picker gives way to an explanation of why.
  const isSafeConnect = Array.isArray(businesses);
  const isOboEmpty =
    isOboEmptyResult({ businesses, outsidePages }) &&
    props.status === MODULE_STATUS.Succeeded &&
    !props.isLoading &&
    !connectedPagesExternalIds.isLoading;
  // Every other login: the one-list picker.
  const isClassicListed =
    !isObo && !isSafeConnect && !!props.authorization && !isLoadingTicks;

  useTrackOboPortfoliosListed({
    businesses,
    pageType: props.pageType,
    isListed: isOboListed,
  });

  const handleOboError = (error: ApiClientError) => {
    // The confirm ran out of time: not a refusal. The warning asks the person
    // to connect again, which is quick once the first try has set things up.
    if (isClientTimeout(error)) {
      toast(t('newSocialMediaProfile.oboOutcome.timedOut'), 'warning');
      return;
    }

    const outcome = error.response?.data?.outcome as string | undefined;
    const messageKey = oboOutcomeMessageKey(outcome);
    const helpUrl = oboOutcomeHelpUrl(outcome);

    if (helpUrl) {
      toast(t(messageKey), 'error', {
        cta: {
          label: t('learnMore'),
          onClick: () => openBlank(helpUrl),
        },
      });
    } else {
      toast(t(messageKey), 'error');
    }
  };

  const handleOboTryAgain = () => {
    const redirectUrl = redirectWithoutUserKey();

    // Off this page's URL too, before the restart: while it is there, the
    // restart's loading state would poll that key again and bring its empty
    // result back.
    setQuery({ userKey: undefined }, 'replaceIn');
    authorize({
      pageType: props.pageType,
      tokenType: 'system_user_obo',
      redirectUrl,
    });
  };

  // Both pickers own their scrolling: every box down to them is a flex
  // column that passes the modal's height cap on (see TwoColumnPicker and
  // SelectSocialProfiles), so only their lists scroll and Add stays in view.
  const fillsModal =
    clsx({
      'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col':
        isOboListed || isClassicListed,
    }) || undefined;

  return (
    <div className={fillsModal}>
      <Modal.Content className={fillsModal}>
        <div
          className={clsx(
            'tw-flex tw-flex-col tw-items-center tw-justify-center',
            // It would make the stacked picker scroll sideways on a phone.
            { 'tw-min-w-[400px]': !isOboListed },
          )}
        >
          {(props.isLoading || isLoadingTicks) && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
              <ActivityIndicator className="tw-h-12 tw-w-12" />
              <p className="tw-mt-6 tw-text-md">
                {t('newSocialMediaProfile.pleaseWait')}
              </p>
            </div>
          )}

          {props.status === MODULE_STATUS.Failed && (
            <>
              <Alert
                variant="danger"
                cta={{
                  label: t('goBack'),
                  onClick: props.onGoBack,
                }}
                title={t('unableToAuthorizeSocialLogin')}
                className="tw-my-5"
              >
                {typeof props.error?.reason === 'string' && (
                  <div>{props.error.reason}</div>
                )}
              </Alert>
              <div className="tw-mt-3 tw-pb-6">
                <ErrorStepsGuide pageType={props.pageType} />
              </div>
              <div className="tw-flex tw-w-full tw-flex-col tw-gap-4 tw-pb-6">
                {!showManualTokenForm ? (
                  <Button
                    variant="ghost"
                    onClick={() => setShowManualTokenForm(true)}
                    data-cy="safe-connect_manual-token-fallback"
                  >
                    {t('newSocialMediaProfile.systemUserToken.fallbackCta')}
                  </Button>
                ) : (
                  <ManualSystemUserTokenForm pageType={props.pageType} />
                )}
              </div>
            </>
          )}
        </div>

        {/* Mounted only once the connected ids are known: the picker reads
            its default ticks once. */}
        {isOboListed && (
          <TwoColumnPicker
            data={oboPicker.pages}
            portfolios={businesses ?? []}
            isPartial={oboPicker.isPartial}
            pageType={props.pageType}
            connectedPagesExternalIds={connectedPagesExternalIds.data || []}
            isReconnect={!!props.isReconnect}
            targetExternalId={targetExternalId}
            userKey={userKey}
            clientBusinessId={clientBusinessId}
            onPagesCreate={props.onPageAdded}
            onError={handleOboError}
          />
        )}

        {isOboEmpty && (
          <OboEmptyState
            reason={props.emptyReason}
            pageType={props.pageType}
            onTryAgain={handleOboTryAgain}
          />
        )}

        {isClassicListed && props.authorization && (
          <GenericProfilesSelectForm
            connectedPagesExternalIds={connectedPagesExternalIds.data || []}
            page={{
              type: props.pageType,
              id: props.pageId,
            }}
            data={props.authorization.data}
            isPartial={props.authorization.partial}
            clientBusinessId={clientBusinessId}
            isSingleSelect={props.isSingleSelect}
            isReconnect={props.isReconnect}
            targetExternalId={targetExternalId}
            onPagesCreate={props.onPageAdded}
            onError={(e) => {
              toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
            }}
          />
        )}
      </Modal.Content>
    </div>
  );
};

export default Form;
